/**
 * Remembers that an import is being parsed, so leaving the page doesn't lose
 * track of it.
 *
 * The WORK already survives — the parse runs on the job queue and its session
 * row outlives the request, the tab, and the browser. What did not survive was
 * the client's knowledge of it: the import screen holds the session id in a
 * provider scoped to that route, so navigating away discarded it and nothing
 * ever said the parse had finished.
 *
 * One id in localStorage is enough to fix that, and is deliberately all this
 * stores. Everything else — stage, progress, the result — is read from the
 * server, which is the only thing that actually knows.
 *
 * ── Why localStorage and not the server ────────────────────────────────────
 * Asking the server "does this user have an import running" would also survive
 * a different browser or device. It would need a new endpoint on
 * sire-import.routes.ts, and the ONE id here covers what people actually do:
 * start an import, wander off to another page, come back. A stale id costs
 * nothing — the watcher clears it as soon as a poll says the session is gone.
 *
 * Every access is wrapped: localStorage throws in private windows and when a
 * browser is set to block site data, and an import must not fail because of
 * that. Losing the id degrades to today's behaviour, not to an error.
 */

const KEY = "icheck:sire-import:active-session";

/** Note that a parse is running, so it can be picked up from any page. */
export function rememberActiveImport(sessionId: number): void {
  try {
    localStorage.setItem(KEY, String(sessionId));
  } catch {
    // Storage unavailable — the import still works, it just won't follow the
    // user off the page.
  }
}

/** The parse being watched, if any. */
export function readActiveImport(): number | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Stop watching — the parse finished, failed, was abandoned, or is gone. */
export function forgetActiveImport(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do: an id we cannot clear is still harmless. */
  }
}
